import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  InputAdornment,
  Alert,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  Container,
  Fab,
  Stack,
  IconButton,
  Badge,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Description,
  AttachFile,
  Add,
  Delete,
  Visibility,
  Notifications,
  AccountCircle,
  Dashboard,
  Search,
  FilterList,
  Schedule,
  Person,
  CalendarToday,
  AccessTime,
  Notes,
  AssignmentTurnedIn,
  WatchLater,
  PictureAsPdf,
  Image,
  InsertDriveFile,
  VideoFile,
  AudioFile,
  Download,
  OpenInNew,
  Assignment
} from '@mui/icons-material';
import { IonPage } from '@ionic/react';

// Mock data for tasks assigned to this user (in real app, this would come from an endpoint)
const assignedTasks = [
  {
    id: 1,
    title: 'Quarterly Report Preparation',
    description: 'Prepare and submit quarterly performance report for the engineering department',
    assignedTo: [1],
    dueDate: '2024-02-15',
    priority: 'high',
    status: 'in-progress',
    createdAt: '2024-01-20',
    assignedBy: 'Admin User'
  },
  {
    id: 2,
    title: 'Client Documentation',
    description: 'Complete client documentation for the new project launch',
    assignedTo: [1],
    dueDate: '2024-02-05',
    priority: 'medium',
    status: 'pending',
    createdAt: '2024-01-25',
    assignedBy: 'Project Manager'
  },
  {
    id: 3,
    title: 'Code Review Session',
    description: 'Conduct code review for the new feature implementation',
    assignedTo: [1],
    dueDate: '2024-01-30',
    priority: 'high',
    status: 'pending',
    createdAt: '2024-01-28',
    assignedBy: 'Tech Lead'
  }
];

const ActivityReporting = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  
  const [activities, setActivities] = useState([
    {
      id: 1,
      activityName: 'Team Meeting',
      startTime: '2024-01-15T10:00',
      endTime: '2024-01-15T11:30',
      notes: 'Weekly team sync to discuss project progress and blockers. We reviewed the Q4 deliverables and planned for the upcoming sprint.',
      staffId: 'EMP-001',
      dateSubmitted: '2024-01-15',
      expirationTime: '2024-01-20T18:00',
      reviewedBy: 'Manager Name',
      status: 'completed',
      department: 'Engineering',
      priority: 'High',
      tags: ['Meeting', 'Collaboration', 'Planning'],
      attachments: [
        {
          id: 1,
          name: 'meeting_notes.pdf',
          type: 'pdf',
          size: '2.4 MB',
          uploadDate: '2024-01-15',
          url: '#'
        }
      ],
      relatedTaskId: 1 // Link to assigned task
    }
    // ... other existing activities
  ]);

  const [activityData, setActivityData] = useState({
    activityName: '',
    notes: '',
    attachments: [],
    expirationTime: '',
    relatedTaskId: null // Track if this activity is related to a task
  });

  const [fileNames, setFileNames] = useState([]);

  const handleInputChange = (field) => (event) => {
    setActivityData({
      ...activityData,
      [field]: event.target.value
    });
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFileNames = files.map(file => file.name);
    setFileNames([...fileNames, ...newFileNames]);
    
    setActivityData({
      ...activityData,
      attachments: [...activityData.attachments, ...files]
    });
  };

  const handleSubmit = () => {
    const newActivity = {
      ...activityData,
      id: Date.now(),
      submittedAt: new Date().toISOString(),
      status: 'submitted',
      // Static fields that will come from backend
      startTime: new Date().toISOString(),
      endTime: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(), // +2 hours
      staffId: 'EMP-001',
      dateSubmitted: new Date().toISOString().split('T')[0],
      reviewedBy: 'Pending Review',
      department: 'General',
      priority: 'Medium',
      tags: activityData.relatedTaskId ? ['Task-Related'] : ['General Activity'],
      attachments: fileNames.map((name, index) => ({
        id: index + 1,
        name: name,
        type: getFileType(name),
        size: '1.5 MB',
        uploadDate: new Date().toISOString().split('T')[0],
        url: '#'
      }))
    };
    
    setActivities([newActivity, ...activities]);
    
    // Reset form
    setActivityData({
      activityName: '',
      notes: '',
      attachments: [],
      expirationTime: '',
      relatedTaskId: null
    });
    setFileNames([]);
    
    setDialogOpen(false);
    setTaskDialogOpen(false);
  };

  const getFileType = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension)) return 'image';
    if (['mp4', 'avi', 'mov', 'wmv'].includes(extension)) return 'video';
    if (['mp3', 'wav', 'aac'].includes(extension)) return 'audio';
    if (['doc', 'docx'].includes(extension)) return 'document';
    if (['xls', 'xlsx'].includes(extension)) return 'spreadsheet';
    if (['ppt', 'pptx'].includes(extension)) return 'presentation';
    if (['zip', 'rar', '7z'].includes(extension)) return 'archive';
    return 'file';
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'pdf': return <PictureAsPdf color="error" />;
      case 'image': return <Image color="primary" />;
      case 'video': return <VideoFile color="secondary" />;
      case 'audio': return <AudioFile color="success" />;
      case 'document': return <Description color="info" />;
      case 'spreadsheet': return <Description color="success" />;
      case 'presentation': return <Description color="warning" />;
      case 'archive': return <InsertDriveFile color="action" />;
      default: return <InsertDriveFile color="action" />;
    }
  };

  const removeFile = (index) => {
    const newFileNames = fileNames.filter((_, i) => i !== index);
    const newAttachments = activityData.attachments.filter((_, i) => i !== index);
    setFileNames(newFileNames);
    setActivityData({
      ...activityData,
      attachments: newAttachments
    });
  };

  const handleDeleteActivity = (activityId) => {
    setActivities(activities.filter(activity => activity.id !== activityId));
  };

  const handleViewActivity = (activity) => {
    setSelectedActivity(activity);
    setViewDialogOpen(true);
  };

  const openDialog = () => {
    setActivityData({
      activityName: '',
      notes: '',
      attachments: [],
      expirationTime: '',
      relatedTaskId: null
    });
    setDialogOpen(true);
  };

  const openTaskDialog = (task) => {
    setSelectedTask(task);
    setActivityData({
      activityName: task.title,
      notes: `Working on: ${task.description}`,
      attachments: [],
      expirationTime: '',
      relatedTaskId: task.id
    });
    setTaskDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setTaskDialogOpen(false);
    setActivityData({
      activityName: '',
      notes: '',
      attachments: [],
      expirationTime: '',
      relatedTaskId: null
    });
    setFileNames([]);
  };

  const closeViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedActivity(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'reviewed': return 'info';
      case 'submitted': return 'warning';
      case 'in-progress': return 'warning';
      case 'pending': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'High': return 'error';
      case 'medium': return 'warning';
      case 'Medium': return 'warning';
      case 'low': return 'success';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const formatDateTime = (dateTimeString) => {
    return new Date(dateTimeString).toLocaleString();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateTimeString) => {
    return new Date(dateTimeString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end - start;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const handleDownloadFile = (file) => {
    alert(`Downloading ${file.name}...\n\nIn a real application, this would download the file from: ${file.url}`);
  };

  const handlePreviewFile = (file) => {
    alert(`Opening preview for ${file.name}...\n\nIn a real application, this would open a preview of the file.`);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <IonPage className='pushDown'>
      <Box sx={{ 
        flexGrow: 1,
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
        overflow: 'auto'
      }}>
        {/* Modern App Bar */}
        <AppBar 
          position="static" 
          elevation={0}
          sx={{ 
            backgroundColor: 'white',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Toolbar>
            <Dashboard sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
              Activity Reporter
            </Typography>
            
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <IconButton size="large">
                <Badge badgeContent={3} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
              <IconButton size="large">
                <AccountCircle />
              </IconButton>
            </Stack>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 3
            }}>
              <Box>
                <Typography variant="h4" component="h1" fontWeight="700" gutterBottom>
                  My Work
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Manage assigned tasks and submit activity reports
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={openDialog}
                size="large"
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  boxShadow: '0 4px 14px 0 rgba(0, 119, 255, 0.2)'
                }}
              >
                New Activity
              </Button>
            </Box>

            {/* Stats and Filters */}
            <Card elevation={1} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Stack direction="row" spacing={3}>
                      <Box>
                        <Typography variant="h3" fontWeight="700" color="primary.main">
                          {assignedTasks.length + activities.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Items
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h3" fontWeight="700" color="success.main">
                          {activities.filter(a => a.status === 'completed').length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Completed
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h3" fontWeight="700" color="warning.main">
                          {assignedTasks.filter(t => t.status === 'in-progress').length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          In Progress
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <TextField
                        size="small"
                        placeholder="Search..."
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 250, borderRadius: 2 }}
                      />
                      <Button 
                        startIcon={<FilterList />} 
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                      >
                        Filter
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          {/* Tabs for My Tasks and My Activities */}
          <Card elevation={3}>
            <CardContent sx={{ p: 0 }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange}
                sx={{ 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  px: 3,
                  pt: 2
                }}
              >
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Assignment sx={{ mr: 1 }} />
                      My Tasks
                      <Chip 
                        label={assignedTasks.length} 
                        size="small" 
                        sx={{ ml: 1 }}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  } 
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Description sx={{ mr: 1 }} />
                      My Activities
                      <Chip 
                        label={activities.length} 
                        size="small" 
                        sx={{ ml: 1 }}
                        color="secondary"
                        variant="outlined"
                      />
                    </Box>
                  } 
                />
              </Tabs>

              {/* My Tasks Table */}
              {tabValue === 0 && (
                <TableContainer component={Paper} elevation={0} sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Task Title</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Assigned By</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignedTasks.map((task) => (
                        <TableRow key={task.id} hover>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {task.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {task.description}
                            </Typography>
                          </TableCell>
                          <TableCell>{task.dueDate}</TableCell>
                          <TableCell>
                            <Chip
                              label={task.priority}
                              color={getPriorityColor(task.priority)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={task.status}
                              color={getStatusColor(task.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {task.assignedBy}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => openTaskDialog(task)}
                                sx={{ 
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  fontSize: '0.75rem'
                                }}
                              >
                                Submit Activity
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {assignedTasks.length === 0 && (
                    <Alert severity="info" sx={{ m: 2 }}>
                      No tasks assigned to you at the moment.
                    </Alert>
                  )}
                </TableContainer>
              )}

              {/* My Activities Grid */}
              {tabValue === 1 && (
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    {activities.map((activity) => (
                      <Grid item xs={12} key={activity.id}>
                        <Card 
                          elevation={1}
                          sx={{
                            borderRadius: 3,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              elevation: 4,
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 25px 0 rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" fontWeight="600" gutterBottom>
                                  {activity.activityName}
                                </Typography>
                                
                                {activity.relatedTaskId && (
                                  <Chip 
                                    label="Task-Related" 
                                    color="primary" 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ mb: 1 }}
                                  />
                                )}
                                
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Start Time
                                    </Typography>
                                    <Typography variant="body2">
                                      {formatDateTime(activity.startTime)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      End Time
                                    </Typography>
                                    <Typography variant="body2">
                                      {formatDateTime(activity.endTime)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Staff ID
                                    </Typography>
                                    <Typography variant="body2">
                                      {activity.staffId}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Date Submitted
                                    </Typography>
                                    <Typography variant="body2">
                                      {activity.dateSubmitted}
                                    </Typography>
                                  </Grid>
                                </Grid>

                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Chip 
                                    label={activity.status} 
                                    color={getStatusColor(activity.status)}
                                    size="small"
                                    variant="outlined"
                                  />
                                  {activity.reviewedBy && (
                                    <Typography variant="body2" color="text.secondary">
                                      Reviewed by: {activity.reviewedBy}
                                    </Typography>
                                  )}
                                  {activity.attachments && activity.attachments.length > 0 && (
                                    <Chip 
                                      icon={<AttachFile />}
                                      label={`${activity.attachments.length} files`}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                    />
                                  )}
                                </Stack>
                              </Box>
                              
                              <Stack direction="row" spacing={1}>
                                <IconButton 
                                  onClick={() => handleViewActivity(activity)}
                                  color="primary"
                                  size="small"
                                >
                                  <Visibility />
                                </IconButton>
                                <IconButton 
                                  onClick={() => handleDeleteActivity(activity.id)}
                                  color="error"
                                  size="small"
                                >
                                  <Delete />
                                </IconButton>
                              </Stack>
                            </Box>

                            {activity.notes && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {activity.notes}
                                </Typography>
                              </Box>
                            )}

                            {activity.expirationTime && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Expiration Time
                                </Typography>
                                <Typography variant="body2">
                                  {formatDateTime(activity.expirationTime)}
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {activities.length === 0 && (
                    <Card 
                      elevation={1}
                      sx={{ 
                        borderRadius: 3,
                        textAlign: 'center',
                        py: 8
                      }}
                    >
                      <CardContent>
                        <Description sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No activities yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          Get started by submitting your first activity report
                        </Typography>
                        <Button 
                          variant="contained" 
                          startIcon={<Add />}
                          onClick={openDialog}
                          sx={{ borderRadius: 2 }}
                        >
                          Create Activity
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>

        {/* Floating Action Button for Mobile */}
        <Fab
          color="primary"
          aria-label="add"
          onClick={openDialog}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            display: { xs: 'flex', md: 'none' }
          }}
        >
          <Add />
        </Fab>

        {/* Dialog for New Activity Form (General) */}
        <Dialog 
          open={dialogOpen} 
          onClose={closeDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { 
              borderRadius: 3,
              maxHeight: '90vh',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: 'primary.main',
            color: 'white',
            py: 3
          }}>
            <Typography variant="h5" component="div" fontWeight="600">
              Submit New Activity
            </Typography>
          </DialogTitle>
          
          <DialogContent dividers sx={{ p: 0, overflow: 'auto' }}>
            <Box sx={{ p: 3 }}>
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                Submit your activity reports with detailed notes and attachments
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Activity Name"
                    value={activityData.activityName}
                    onChange={handleInputChange('activityName')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Description color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={4}
                    value={activityData.notes}
                    onChange={handleInputChange('notes')}
                    variant="outlined"
                    placeholder="Enter detailed notes about the activity..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 3, 
                      borderStyle: 'dashed',
                      borderRadius: 2,
                      backgroundColor: 'grey.50'
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <AttachFile color="primary" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        Upload Attachments
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Drag and drop files here or click to browse
                      </Typography>
                      <input
                        accept="*/*"
                        style={{ display: 'none' }}
                        id="file-upload"
                        multiple
                        type="file"
                        onChange={handleFileUpload}
                      />
                      <label htmlFor="file-upload">
                        <Button variant="outlined" component="span" sx={{ borderRadius: 2 }}>
                          Choose Files
                        </Button>
                      </label>
                    </Box>

                    {fileNames.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          Selected Files:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {fileNames.map((fileName, index) => (
                            <Chip
                              key={index}
                              label={fileName}
                              onDelete={() => removeFile(index)}
                              variant="outlined"
                              size="small"
                              sx={{ borderRadius: 1 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={closeDialog} 
              color="inherit"
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSubmit}
              size="large"
              sx={{ 
                borderRadius: 2,
                px: 4,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Submit Activity
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Task-based Activity Submission */}
        <Dialog 
          open={taskDialogOpen} 
          onClose={closeDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { 
              borderRadius: 3,
              maxHeight: '90vh',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: 'info.main',
            color: 'white',
            py: 3
          }}>
            <Typography variant="h5" component="div" fontWeight="600">
              Submit Activity for: {selectedTask?.title}
            </Typography>
          </DialogTitle>
          
          <DialogContent dividers sx={{ p: 0, overflow: 'auto' }}>
            <Box sx={{ p: 3 }}>
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                You are submitting an activity report for the assigned task: <strong>{selectedTask?.title}</strong>
              </Alert>

              <Card sx={{ mb: 3, border: '1px solid', borderColor: 'info.main' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Task Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Description:</strong> {selectedTask?.description}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Due Date:</strong> {selectedTask?.dueDate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Priority:</strong> 
                    <Chip 
                      label={selectedTask?.priority} 
                      color={getPriorityColor(selectedTask?.priority)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                </CardContent>
              </Card>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Activity Name"
                    value={activityData.activityName}
                    onChange={handleInputChange('activityName')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Description color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Activity Notes"
                    multiline
                    rows={4}
                    value={activityData.notes}
                    onChange={handleInputChange('notes')}
                    variant="outlined"
                    placeholder="Describe what you accomplished for this task..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 3, 
                      borderStyle: 'dashed',
                      borderRadius: 2,
                      backgroundColor: 'grey.50'
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <AttachFile color="primary" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        Upload Attachments
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add files related to this task completion
                      </Typography>
                      <input
                        accept="*/*"
                        style={{ display: 'none' }}
                        id="task-file-upload"
                        multiple
                        type="file"
                        onChange={handleFileUpload}
                      />
                      <label htmlFor="task-file-upload">
                        <Button variant="outlined" component="span" sx={{ borderRadius: 2 }}>
                          Choose Files
                        </Button>
                      </label>
                    </Box>

                    {fileNames.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          Selected Files:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {fileNames.map((fileName, index) => (
                            <Chip
                              key={index}
                              label={fileName}
                              onDelete={() => removeFile(index)}
                              variant="outlined"
                              size="small"
                              sx={{ borderRadius: 1 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={closeDialog} 
              color="inherit"
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="info" 
              onClick={handleSubmit}
              size="large"
              sx={{ 
                borderRadius: 2,
                px: 4,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Submit Activity for Task
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Activity Details Dialog (Keep existing) */}
        {/* ... (Keep the existing view dialog code as is) ... */}
      </Box>
    </IonPage>
  );
};

export default ActivityReporting;